import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Download, Sparkles, UserPlus, LogIn, ArrowRight, Share2, Trophy } from "lucide-react";
import BudgetSheet from "@/components/BudgetSheet";
import { BrandLogoImg } from "@/components/BrandLogoImg";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, unauthenticated read-only view of a budget.
 * Opened by schools who click the WhatsApp link before registering.
 * - Shows the full budget using <BudgetSheet /> (same visual as the private view).
 * - Prominent top banner invites the school to join AMC Escolas.
 * - Photos are fetched through /api/public/budgets/:id/files/:path (validated server-side).
 */
export default function PublicBudgetView() {
  const { id } = useParams();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const { user, loading: authLoading } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND}/api/public/budgets/${id}`);
      // Rewrite photo paths so <img> inside BudgetSheet uses the public file endpoint.
      const rewritePath = (p) =>
        p ? `${BACKEND}/api/public/budgets/${id}/files/${p}` : p;
      const b = r.data || {};
      const items = (b.items || []).map((it) => ({
        ...it,
        before_photo_url: rewritePath(it.before_photo_path),
        after_photo_url: rewritePath(it.after_photo_path),
      }));
      const generalPhotoUrls = (b.general_photos || []).map(rewritePath);
      setBudget({ ...b, items, _general_photo_urls: generalPhotoUrls });
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    window.scrollTo({ top: 0 });
  }, [id]);

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/public/budgets/${id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orcamento_${budget?.number || id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao gerar PDF");
    }
  };

  const sharePdf = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/public/budgets/${id}/pdf`, {
        responseType: "blob",
      });
      const file = new File([r.data], `orcamento_${budget?.number || id}.pdf`, {
        type: "application/pdf",
      });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Orçamento ${budget?.number}` });
      } else {
        downloadPdf();
      }
    } catch {
      toast.error("Falha ao compartilhar");
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-500">Carregando orçamento…</div>
      </div>
    );
  }

  if (notFound || !budget) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="font-heading text-2xl font-black text-slate-900 mb-2">
            Orçamento não encontrado
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            O link que você acessou pode ter expirado ou ser inválido.
          </p>
          <Link to="/">
            <Button variant="outline" data-testid="public-back-home">
              Voltar para o início
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const school = budget.school || {};
  if (user?.role === "admin") return <Navigate to={`/orcamentos/${budget.id}`} replace />;
  if (user?.role === "school" && school.id && user.school_id === school.id && !budget.parent_budget_id) {
    return <Navigate to={`/escola/orcamentos/${budget.id}`} replace />;
  }
  const hasUsers = !!school.has_users;
  const inviteToken = school.invite_token;
  const inviteUrl = inviteToken ? `/convite/${inviteToken}` : null;
  const concList = Array.isArray(budget.concorrencias) ? budget.concorrencias : [];
  const generalPhotoUrls = budget._general_photo_urls || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top brand bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="public-home-link">
            <BrandLogoImg
              alt="AMC Escolas"
              className="w-9 h-9 rounded-full object-contain"
            />
            <div className="min-w-0">
              <p className="font-heading text-sm font-black text-[#1e3a8a] leading-tight">
                AMC Escolas
              </p>
              <p className="text-[10px] text-slate-500 leading-tight">
                Manutenção e Serviços
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadPdf}
              data-testid="public-download-pdf"
            >
              <Download className="w-4 h-4 mr-1.5" />
              PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={sharePdf}
              data-testid="public-share-pdf"
              className="hidden sm:inline-flex"
            >
              <Share2 className="w-4 h-4 mr-1.5" />
              Compartilhar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 md:py-6 space-y-4">
        {/* === Join CTA banner === */}
        {!hasUsers && inviteUrl ? (
          <div
            className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 p-4 md:p-5 shadow-sm"
            data-testid="public-join-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-heading text-base md:text-lg font-black text-amber-900 leading-tight">
                  Faça parte do time AMC Escolas 🎉
                </h2>
                <p className="text-xs md:text-sm text-amber-800 mt-1 leading-snug">
                  Cadastre sua escola em menos de 1 minuto e tenha acesso completo:
                  acompanhar orçamentos em tempo real, aprovar pelo WhatsApp,
                  consultar preços médios e ver o histórico da sua escola.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to={inviteUrl}>
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="public-cta-register"
                    >
                      <UserPlus className="w-4 h-4 mr-1.5" />
                      Cadastrar escola
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                  <Link to="/escola/login">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-400 text-amber-800 hover:bg-amber-100"
                      data-testid="public-cta-login"
                    >
                      <LogIn className="w-4 h-4 mr-1.5" />
                      Já tenho cadastro
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="rounded-2xl border border-blue-200 bg-blue-50 p-3 md:p-4 flex items-start gap-3"
            data-testid="public-login-banner"
          >
            <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0">
              <LogIn className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-sm font-bold text-blue-900">
                Entre para aprovar, comentar e acompanhar
              </p>
              <p className="text-xs text-blue-800 mt-0.5">
                Esta escola já faz parte do AMC Escolas. Entre com seu usuário
                para aprovar o orçamento e ter acesso ao histórico completo.
              </p>
              <div className="mt-2">
                <Link to="/escola/login">
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="public-cta-login-only"
                  >
                    <LogIn className="w-4 h-4 mr-1.5" />
                    Entrar
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* === Read-only note === */}
        <div
          className="text-[11px] md:text-xs text-slate-500 text-center"
          data-testid="public-readonly-note"
        >
          Visualização pública · somente leitura — para aprovar, comentar ou
          acompanhar atualizações, cadastre-se ou entre no sistema.
        </div>

        {/* === The budget sheet (same visual as the private view) === */}
        <BudgetSheet budget={budget} />

        {/* === General photos (if any) === */}
        {generalPhotoUrls.length > 0 && (
          <section
            className="bg-white rounded-2xl border border-slate-200 card-shadow p-4 md:p-6"
            data-testid="public-general-photos"
          >
            <h3 className="font-heading text-base font-bold text-slate-800 mb-3">
              Fotos do orçamento
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {generalPhotoUrls.map((u, i) => (
                <a
                  key={i}
                  href={u}
                  target="_blank"
                  rel="noreferrer"
                  className="aspect-square rounded-lg overflow-hidden bg-slate-100 block"
                  data-testid={`public-photo-${i}`}
                >
                  <img
                    src={u}
                    alt={`Foto ${i + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* === Concorrências (if any) === */}
        {concList.length > 0 && (
          <section
            id="public-concorrencias"
            className="bg-white rounded-2xl border border-amber-200 card-shadow p-4 md:p-6"
            data-testid="public-concorrencias"
          >
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-amber-600" />
              <h3 className="font-heading text-base font-bold text-amber-900">
                Concorrências vinculadas ({concList.length})
              </h3>
            </div>
            <p className="text-xs text-slate-600">
              Para visualizar o detalhe de cada concorrência,{" "}
              {inviteUrl ? (
                <Link to={inviteUrl} className="text-amber-700 underline font-semibold">
                  cadastre sua escola
                </Link>
              ) : (
                <Link to="/escola/login" className="text-amber-700 underline font-semibold">
                  entre no sistema
                </Link>
              )}
              .
            </p>
          </section>
        )}

        {/* === Bottom secondary CTA === */}
        {!hasUsers && inviteUrl && (
          <div
            className="rounded-2xl bg-slate-900 text-white p-5 text-center"
            data-testid="public-bottom-cta"
          >
            <p className="font-heading text-base md:text-lg font-black mb-1">
              Gostou? Entre no time AMC Escolas.
            </p>
            <p className="text-xs md:text-sm text-slate-300 mb-3">
              Acompanhe seus orçamentos, aprove serviços e tenha o histórico da
              sua escola na palma da mão.
            </p>
            <Link to={inviteUrl}>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="public-bottom-cta-btn"
              >
                <UserPlus className="w-4 h-4 mr-1.5" />
                Cadastrar agora
              </Button>
            </Link>
          </div>
        )}

        <div className="text-center text-[10px] text-slate-400 py-4">
          © AMC Manutenção e Serviços LTDA-ME · amcescolas@gmail.com
        </div>
      </main>
    </div>
  );
}
